// src/components/cognition/cognition-model-modal.tsx
import { CheckCircle2, Circle, Check, Sparkles } from "lucide-react";
import { Button, Modal } from "../kit";

type AIModel = {
  id: string;
  name: string;
  provider: string;
  description?: string;
  recommended: boolean;
  isOptional?: boolean;
};

export function CognitionModelModal({
  open,
  onOpenChange,
  aiModels,
  selectedModels,
  onToggleModel,
  onUseRecommended,
  onSelectAll,
  onClearAll,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  aiModels: AIModel[];
  selectedModels: string[];
  onToggleModel: (id: string) => void;
  onUseRecommended: () => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      size="form"
      title="Choose AI Models"
      description="Select which AI models to include in your cognition analysis"
      actions={
        <>
          <span className="k-meta">
            {selectedModels.length} model{selectedModels.length !== 1 ? "s" : ""} selected
          </span>
          <Button icon={Check} onClick={() => onOpenChange(false)}>Done</Button>
        </>
      }
    >
          <div className="upm-row">
            <Button variant="secondary" icon={Sparkles} onClick={onUseRecommended}>
              Use Recommended
            </Button>
            <Button variant="ghost" onClick={onSelectAll}>Select All</Button>
            <Button variant="ghost" onClick={onClearAll}>Clear All</Button>
          </div>

        <div className="upm-modal-scroll">
          <div className="space-y-2">
            {aiModels.map((model) => {
              const isSelected = selectedModels.includes(model.id);

              return (
                <button
                  key={model.id}
                  onClick={() => onToggleModel(model.id)}
                  className="w-full text-left p-4 rounded-lg border transition-all"
                  style={{
                    backgroundColor: isSelected ? "var(--alpha-teal-2)" : "var(--card)",
                    borderColor: isSelected ? "var(--accent)" : "var(--border)",
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 pt-0.5">
                      {isSelected ? (
                        <CheckCircle2 className="w-5 h-5 text-accent" />
                      ) : (
                        <Circle className="w-5 h-5 text-muted-foreground" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <h4 style={{ fontSize: "var(--text-base)", fontWeight: "var(--font-weight-semibold)" }}>
                          {model.name}
                        </h4>

                        {model.recommended && (
                          <span
                            className="px-2 py-0.5 rounded text-accent border-0"
                            style={{
                              backgroundColor: "var(--alpha-teal-10)",
                              fontSize: "var(--text-sm)",
                              fontWeight: "var(--font-weight-semibold)",
                            }}
                          >
                            Recommended
                          </span>
                        )}

                        {model.isOptional && (
                          <span
                            className="px-2 py-0.5 rounded border"
                            style={{
                              backgroundColor: "var(--muted)",
                              borderColor: "var(--border)",
                              fontSize: "var(--text-sm)",
                              color: "var(--muted-foreground)",
                              fontWeight: "var(--font-weight-semibold)",
                            }}
                          >
                            Optional
                          </span>
                        )}
                      </div>

                      <p className="text-muted-foreground" style={{ fontSize: "var(--text-sm)" }}>
                        {model.provider}
                      </p>

                      {!!model.description && (
                        <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", marginTop: 6 }}>
                          {model.description}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

    </Modal>
  );
}
